import styled from 'styled-components';

const colors = {
  primary: '#00d4ff',
  surface: 'rgba(0, 0, 0, 0.3)',
  border: 'rgba(0, 212, 255, 0.15)',
  borderHover: 'rgba(0, 212, 255, 0.3)',
  text: '#ffffff',
  textMuted: '#8bb8d8'
};

export const AnalyticsContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 100px 20px 40px 20px;
  color: ${colors.text};
  font-weight: bold;
`;

export const AnalyticsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 20px;
`;

export const PageTitle = styled.h1`
  font-family: 'Rajdhani', sans-serif;
  font-size: 2.5rem;
  color: ${colors.primary};
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 3px;
  margin: 0;
`;

export const AnalyticsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

export const ChartCard = styled.div`
  background: ${colors.surface};
  border: 1px solid ${colors.border};
  border-radius: 12px;
  padding: 24px;
`;

export const ChartTitle = styled.h3`
  font-family: 'Rajdhani', sans-serif;
  font-size: 1.3rem;
  color: ${colors.primary};
  text-transform: uppercase;
  letter-spacing: 1px;
  margin: 0 0 20px 0;
`;

export const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

export const MetricCard = styled.div`
  background: ${colors.surface};
  border: 1px solid ${colors.border};
  border-radius: 12px;
  padding: 24px;
  text-align: center;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${colors.borderHover};
  }
`;

export const MetricValue = styled.div`
  font-family: 'Rajdhani', sans-serif;
  font-size: 2.5rem;
  font-weight: 700;
  color: ${colors.primary};
`;

export const MetricLabel = styled.div`
  font-family: 'Courier New', monospace;
  font-size: 0.85rem;
  font-weight: bold;
  color: ${colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-top: 8px;
`;

export const FullWidthChart = styled(ChartCard)`
  grid-column: 1 / -1;
`;

export const SectionTitle = styled.h2`
  font-family: 'Rajdhani', sans-serif;
  font-size: 1.5rem;
  color: ${colors.primary};
  text-transform: uppercase;
  letter-spacing: 2px;
  margin: 40px 0 20px 0;
  padding-bottom: 10px;
  border-bottom: 1px solid ${colors.border};
`;

export const DatePickerWrapper = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;

  input {
    background: rgba(0, 0, 0, 0.4);
    border: 1px solid ${colors.borderHover};
    border-radius: 6px;
    padding: 8px 12px;
    color: ${colors.text};
    font-family: 'Courier New', monospace;
    font-size: 0.85rem;
    font-weight: bold;
    outline: none;
    transition: all 0.2s ease;

    &:focus {
      border-color: ${colors.primary};
    }

    &::-webkit-calendar-picker-indicator {
      filter: invert(1);
      cursor: pointer;
    }
  }

  span {
    color: ${colors.textMuted};
    font-family: 'Courier New', monospace;
    font-weight: bold;
  }
`;

export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  gap: 20px;
`;

export const Spinner = styled.div`
  width: 60px;
  height: 60px;
  border: 4px solid rgba(0, 212, 255, 0.1);
  border-top: 4px solid ${colors.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

export const LoadingText = styled.div`
  color: ${colors.primary};
  font-family: 'Courier New', monospace;
  font-size: 1rem;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

export const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  color: #f44336;
  font-family: 'Courier New', monospace;
  font-size: 1rem;
  text-align: center;
  gap: 16px;
`;

export const NoAccessContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
  color: ${colors.text};
  font-family: 'Courier New', monospace;
  text-align: center;

  h2 {
    font-size: 1.5rem;
    color: ${colors.primary};
    margin-bottom: 16px;
    text-transform: uppercase;
  }

  p {
    font-size: 0.9rem;
    color: ${colors.textMuted};
  }
`;

export const TopPlayersTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: 'Courier New', monospace;

  th, td {
    padding: 10px 8px;
    text-align: left;
  }

  th {
    color: ${colors.primary};
    font-size: 0.85rem;
    font-weight: bold;
    text-transform: uppercase;
    border-bottom: 1px solid ${colors.borderHover};
  }

  td {
    color: ${colors.text};
    font-size: 0.9rem;
    font-weight: bold;
    border-bottom: 1px solid rgba(0, 212, 255, 0.1);
  }

  tr:hover td {
    background: rgba(0, 212, 255, 0.05);
  }
`;

export const BackButton = styled.button`
  background: rgba(0, 212, 255, 0.15);
  border: 1px solid ${colors.primary};
  border-radius: 6px;
  padding: 8px 20px;
  color: ${colors.primary};
  font-family: 'Courier New', monospace;
  font-size: 0.85rem;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 212, 255, 0.25);
  }
`;

// Tooltip components
export const TooltipWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

export const TooltipIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: rgba(0, 212, 255, 0.2);
  border: 1px solid rgba(0, 212, 255, 0.4);
  color: ${colors.primary};
  font-size: 12px;
  font-weight: bold;
  cursor: help;
  margin-left: 8px;

  &:hover + div {
    opacity: 1;
    visibility: visible;
  }
`;

export const TooltipContent = styled.div`
  position: absolute;
  left: 50%;
  top: 100%;
  transform: translateX(-50%);
  margin-top: 8px;
  padding: 12px 16px;
  background: rgba(0, 0, 0, 0.95);
  border: 1px solid ${colors.borderHover};
  border-radius: 8px;
  color: ${colors.text};
  font-family: 'Courier New', monospace;
  font-size: 0.8rem;
  font-weight: normal;
  line-height: 1.5;
  white-space: nowrap;
  z-index: 100;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s ease, visibility 0.2s ease;

  &::before {
    content: '';
    position: absolute;
    top: -6px;
    left: 50%;
    transform: translateX(-50%);
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-bottom: 6px solid ${colors.borderHover};
  }
`;

export const TitleWithTooltip = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
`;
